/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { AppType } from 'src/core/stores/app/types';
import { RootStore } from 'src/core/stores/RootStore';
import { Handler, HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addToast } from 'src/core/utils/decorators/addToast';
import { addTracker } from 'src/core/utils/decorators/addTracker';

/**
 * This function does the following:
 *
 * - Attaches/joins context parameters with every incoming dispatch
 *   request to ensure that each request has a service key attached
 *   to it for later use with services.
 *
 * - Executes common decorators that will usually be needed with
 *   every request passing through the dispatcher.
 *
 * @param handler The handler function to execute.
 * @param params The parameters passed to this handler.
 * @param dependencies The dependencies that are used to
 * construct handlers
 */
export const attachHandlerMiddleware = async <T>(handler: Handler, params: HandlerParams<T>, dependencies: HandlerGroupFactoryDeps) => {
    const paramsWithContext = attachHandlerContext(params, dependencies.rootStore);

    await flow(handler, addToast(paramsWithContext), addTracker(paramsWithContext))(paramsWithContext);
};

/**
 * Attaches context information with the request
 * to the handlers.
 *
 * @param params The original parameters from the component.
 * @param rootStore The root store that contains the app state.
 * @returns Params with context.
 */
const attachHandlerContext = <T>(params: HandlerParams<T>, rootStore: RootStore): HandlerParams<T> => {
    if (!params) {
        params = <T>{};
    }

    const appIdToUse = params.context?.appId || rootStore.currentLuisAppStore?.id || '';
    const appTypeToUse: AppType = params.context?.appType || rootStore.currentAppStore?.appType;
    const versionIdToUse = params.context?.versionId || rootStore.currentLuisAppStore?.currentAppVersionStore?.id || '';

    if (!params.context) {
        params.context = {};
    }

    params.context.appId = appIdToUse;
    params.context.versionId = versionIdToUse;
    params.context.appType = appTypeToUse;

    return params;
};
